import {addToast} from '@albert-io/atomic';
import {debounce} from 'lodash';
import React from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';

import constants from 'client/constants';
import {AppError, normalizeError} from 'lib/normalizeError';

export type ResetPasswordState = {
  confirmInputValue: string;
  disableSubmit: boolean;
  doPasswordsMatch: boolean;
  passwordInputValue: string;
  inputType: 'password' | 'text';
  isPasswordValid: boolean;
  isRequestInflight: boolean;
  customDisableSubmitCondition: boolean;
};

export type ResetPasswordActions = {
  setInputType: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleConfirmPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (props: {
    onSubmit: (password: string) => Promise<void>;
    onError: (error: AppError) => void;
    onSubmitSuccess: () => void;
  }) => Promise<void>;
  setCustomDisableSubmitCondition: (condition: boolean) => void;
  reset: () => void;
};

const initialState: ResetPasswordState = {
  confirmInputValue: '',
  disableSubmit: true,
  doPasswordsMatch: false,
  passwordInputValue: '',
  inputType: 'password',
  isPasswordValid: true,
  isRequestInflight: false,
  customDisableSubmitCondition: false
};

// Helper function to compute whether submit should be disabled
const computeDisableSubmit = (state: ResetPasswordState): boolean => {
  const {
    doPasswordsMatch,
    isPasswordValid,
    isRequestInflight,
    passwordInputValue,
    confirmInputValue,
    customDisableSubmitCondition
  } = state;
  const hasEnteredAndConfirmedPassword = passwordInputValue && confirmInputValue;

  return (
    isRequestInflight ||
    customDisableSubmitCondition ||
    !hasEnteredAndConfirmedPassword ||
    !doPasswordsMatch ||
    !isPasswordValid
  );
};

const validatePasswordDebounced = debounce((passwordRegex: RegExp, val: string, set: any) => {
  set(
    (state: ResetPasswordState & ResetPasswordActions) => {
      state.isPasswordValid = passwordRegex.test(val);
      state.disableSubmit = computeDisableSubmit(state);
    },
    false,
    'validatePassword'
  );
}, 500);

const validateConfirmPasswordDebounced = debounce(
  (val: string, passwordInputValue: string, set: any) => {
    set(
      (state: ResetPasswordState & ResetPasswordActions) => {
        state.doPasswordsMatch = val === passwordInputValue;
        state.disableSubmit = computeDisableSubmit(state);
      },
      false,
      'validateConfirmPassword'
    );
  },
  500
);

export const useResetPasswordStore = create<ResetPasswordState & ResetPasswordActions>()(
  devtools(
    immer((set, get) => ({
      ...initialState,

      setInputType: (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputType = e.target.checked ? 'text' : 'password';
        set(
          (state) => {
            state.inputType = inputType;
          },
          false,
          'setInputType'
        );
      },

      handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = e.target;
        set(
          (state) => {
            state.passwordInputValue = value;
            state.confirmInputValue = '';
            state.doPasswordsMatch = false;
            state.disableSubmit = computeDisableSubmit(state);
          },
          false,
          'handlePasswordChange'
        );
        const passwordRegex = constants.REGEX_COLLECTION.PASSWORD;
        validatePasswordDebounced(passwordRegex, value, set);
      },

      handleConfirmPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const {value} = e.target;
        const {passwordInputValue} = get();
        set(
          (state) => {
            state.confirmInputValue = value;
            state.disableSubmit = computeDisableSubmit(state);
          },
          false,
          'handleConfirmPasswordChange'
        );
        validateConfirmPasswordDebounced(value, passwordInputValue, set);
      },

      handleSubmit: async ({onSubmit, onError, onSubmitSuccess}) => {
        const state = get();
        if (state.disableSubmit) {
          return;
        }

        set(
          (draft) => {
            draft.isRequestInflight = true;
            draft.disableSubmit = computeDisableSubmit(draft);
          },
          false,
          'handleSubmit:start'
        );

        const value = state.passwordInputValue;

        try {
          await onSubmit(value);
          addToast({
            title: 'Success!',
            color: 'positive',
            message: `Your changes have been saved.`
          });
          onSubmitSuccess();
        } catch (error) {
          onError(normalizeError(error));
        }

        set(
          (draft) => {
            draft.isRequestInflight = false;
            draft.disableSubmit = computeDisableSubmit(draft);
          },
          false,
          'handleSubmit:end'
        );
      },

      setCustomDisableSubmitCondition: (condition: boolean) => {
        set(
          (state) => {
            state.customDisableSubmitCondition = condition;
            state.disableSubmit = computeDisableSubmit(state);
          },
          false,
          'setCustomDisableSubmitCondition'
        );
      },

      reset: () => {
        validatePasswordDebounced.cancel();
        validateConfirmPasswordDebounced.cancel();
        set(
          (state) => {
            Object.assign(state, initialState);
          },
          false,
          'reset'
        );
      }
    })),
    {name: 'ResetPasswordStore'}
  )
);
